import { httpClient } from "@/shared/api";

import type { MasterServiceConfig, MasterServiceConfigInput, ServiceMasterLink, ServiceMastersResult } from "../model/types";
import type {
  MasterServiceLinkDTO,
  MasterServicesResponseDTO,
  MasterSideConfigResponseDTO,
  ServiceMasterLinkDTO,
  ServiceMastersResponseDTO,
  ServiceSideConfigResponseDTO,
  SetMasterServiceConfigRequestDTO,
  SetServiceMasterConfigRequestDTO,
} from "./dto";

/** DTO (snake_case) → доменний `MasterServiceConfig` (camelCase). Єдина точка мапінгу. */
function toDomain(dto: MasterServiceLinkDTO): MasterServiceConfig {
  return {
    serviceId: dto.service_id,
    name: dto.name,
    durationMin: dto.duration_min,
    priceCents: dto.price_cents,
    bufferMin: dto.buffer_min,
  };
}

/** `GET /api/masters/{id}/services` — послуги майстра з конфігами. */
export async function listMasterServices(
  masterId: string,
): Promise<MasterServiceConfig[]> {
  const { data } = await httpClient.get<MasterServicesResponseDTO>(
    `/masters/${masterId}/services`,
  );
  return data.services.map(toDomain);
}

/**
 * `PUT /api/masters/{masterId}/services/{serviceId}` — встановити/оновити конфіг.
 * Завжди шле `price_cents`/`buffer_min` явними integers (0 якщо не задано) —
 * не покладаємось на серверний default.
 *
 * Валідація (NaN/Infinity) — відповідальність composable (t3), перед викликом.
 */
export async function setMasterService(
  masterId: string,
  serviceId: string,
  input: MasterServiceConfigInput,
): Promise<MasterServiceConfig> {
  const body: SetMasterServiceConfigRequestDTO = {
    duration_min: input.durationMin,
    price_cents: input.priceCents,
    buffer_min: input.bufferMin,
  };
  const { data } = await httpClient.put<MasterSideConfigResponseDTO>(
    `/masters/${masterId}/services/${serviceId}`,
    body,
  );
  return toDomain(data);
}

/** `DELETE /api/masters/{masterId}/services/{serviceId}` → 204 без тіла. */
export async function removeMasterService(
  masterId: string,
  serviceId: string,
): Promise<void> {
  await httpClient.delete(`/masters/${masterId}/services/${serviceId}`);
}

// ── Service-side API (spec §6.1, 169-T2) ────────────────────────────────────

/** DTO → `ServiceMasterLink`. Єдина точка мапінгу для service-side відповіді. */
function toServiceMasterLink(dto: ServiceMasterLinkDTO): ServiceMasterLink {
  return {
    masterId: dto.master_id,
    durationMin: dto.duration_min,
    priceCents: dto.price_cents,
    bufferMin: dto.buffer_min,
  };
}

/** `GET /api/services/{serviceId}/masters` — майстри, призначені до послуги. */
export async function listServiceMasters(
  serviceId: string,
): Promise<ServiceMastersResult> {
  const { data } = await httpClient.get<ServiceMastersResponseDTO>(
    `/services/${serviceId}/masters`,
  );
  return {
    masters: data.masters.map(toServiceMasterLink),
    fromPriceCents: data.from_price_cents,
    fromDurationMin: data.from_duration_min,
  };
}

/**
 * `PUT /api/services/{serviceId}/masters/{masterId}` — встановити/оновити конфіг.
 * Завжди шле явні integers (`price_cents`/`buffer_min` = 0 якщо не задано).
 */
export async function setServiceMaster(
  serviceId: string,
  masterId: string,
  input: MasterServiceConfigInput,
): Promise<ServiceMasterLink> {
  const body: SetServiceMasterConfigRequestDTO = {
    duration_min: input.durationMin,
    price_cents: input.priceCents,
    buffer_min: input.bufferMin,
  };
  const { data } = await httpClient.put<ServiceSideConfigResponseDTO>(
    `/services/${serviceId}/masters/${masterId}`,
    body,
  );
  return toServiceMasterLink(data);
}

/** `DELETE /api/services/{serviceId}/masters/{masterId}` → 204 без тіла. */
export async function removeServiceMaster(
  serviceId: string,
  masterId: string,
): Promise<void> {
  await httpClient.delete(`/services/${serviceId}/masters/${masterId}`);
}
